import type {
  AnalyticsMetricName,
  AnalyticsRange,
  AnalyticsSplitName,
  StaffAnalyticsOverview,
} from "@lafayette/schemas";
import { type ReactNode, useId } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { ActivityChart } from "../charts/ActivityChart";
import { Donut, type DonutSlice } from "../charts/Donut";
import { SpendChart } from "../charts/SpendChart";
import { countText, durationText, moneyText, percentText } from "../format";
import {
  biggestDrop,
  funnelRows,
  messageRows,
  REQUEST_TYPE_LABELS,
  rangePhrase,
} from "../overview-model";
import { useZoomOnClick, ZoomInButton, zoomHref } from "./ZoomIn";

// The cards of the Overview that read records (Paper "Activity over time", "Request funnel", "What <customer> asks for", "Spend" and "Assistant
// and concierge"). Each card is a section with its own heading and writes every number as text beside its chart.

type Records = StaffAnalyticsOverview["records"];

/** The metric that Explore opens with when a card is clicked, and the range it keeps. */
type Zoom = Readonly<{
  metric: AnalyticsMetricName;
  range: AnalyticsRange;
  split?: AnalyticsSplitName;
}>;

export function CardShell({
  title,
  description,
  children,
  className,
  zoom,
}: Readonly<{
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  zoom: Zoom;
}>) {
  const titleId = useId();
  const href = zoomHref(zoom.metric, zoom.range, zoom.split);
  const onClick = useZoomOnClick(href);
  return (
    <Card
      onClick={onClick}
      className={cn(
        "group cursor-pointer rounded-2xl hover:border-primary focus-within:border-primary",
        className,
      )}
    >
      <section aria-labelledby={titleId}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="grid gap-1">
              <CardTitle as="h2" id={titleId} className="text-xl leading-6">
                {title}
              </CardTitle>
              {description === undefined ? null : (
                <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
                  {description}
                </p>
              )}
            </div>
            <ZoomInButton href={href} label={title} />
          </div>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </section>
    </Card>
  );
}

export function ActivityCard({
  records,
  tz,
  range,
}: Readonly<{ records: Records; tz: string; range: AnalyticsRange }>) {
  return (
    <CardShell
      title="Activity over time"
      description="Messages from the customer, with their requests, payments and trips"
      className="lg:col-span-3"
      zoom={{ metric: "customer_messages", range }}
    >
      <ActivityChart activity={records.activity} tz={tz} />
    </CardShell>
  );
}

export function FunnelCard({
  records,
  range,
}: Readonly<{ records: Records; range: AnalyticsRange }>) {
  const rows = funnelRows(records.requestFunnel);
  const drop = biggestDrop(rows);
  return (
    <CardShell
      title="Request funnel"
      description="Requests submitted in this period and how far they got"
      zoom={{ metric: "requests_created", range }}
    >
      <ul className="m-0 grid list-none gap-2.5 p-0">
        {rows.map((row) => (
          <li key={row.key} className="grid grid-cols-[5.5rem_1fr_auto] items-center gap-3 text-sm">
            <span className="text-muted-foreground">{row.label}</span>
            <span aria-hidden="true" className="h-3 rounded-sm bg-muted">
              <span
                className="block h-3 rounded-sm bg-primary"
                style={{ width: `${Math.round(row.share * 100)}%` }}
              />
            </span>
            <span className="text-right">
              <span className="font-medium">{row.count}</span>
              <span className="text-muted-foreground"> · {percentText(row.share)}</span>
            </span>
          </li>
        ))}
      </ul>
      <dl className="m-0 grid grid-cols-2 gap-3 border-t border-hairline pt-3 text-sm">
        <div>
          <dt className="text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase">
            Median time to quote
          </dt>
          <dd className="m-0 font-serif text-lg font-medium">
            {records.medianSecondsToQuote === null
              ? "No quote yet"
              : durationText(records.medianSecondsToQuote)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase">
            Biggest drop
          </dt>
          <dd className="m-0 font-serif text-lg font-medium">
            {drop === null ? "None" : `${drop.from} to ${drop.to}: ${drop.lost}`}
          </dd>
        </div>
      </dl>
    </CardShell>
  );
}

// Whole class names, written out: Tailwind finds a class only when it is in the source in full. The ring uses the stroke, the legend the fill.
const TYPE_COLOURS: Readonly<
  Record<keyof typeof REQUEST_TYPE_LABELS, Readonly<{ stroke: string; fill: string }>>
> = {
  DINING: { stroke: "stroke-primary", fill: "fill-primary" },
  VILLA: { stroke: "stroke-brass", fill: "fill-brass" },
  EVENT: { stroke: "stroke-info", fill: "fill-info" },
  EXPERIENCE: { stroke: "stroke-success", fill: "fill-success" },
  SERVICE: { stroke: "stroke-copilot", fill: "fill-copilot" },
  TRANSPORT: { stroke: "stroke-warning", fill: "fill-warning" },
  OTHER: { stroke: "stroke-neutral-dot", fill: "fill-neutral-dot" },
};

export function RequestTypesCard({
  records,
  name,
  range,
}: Readonly<{ records: Records; name: string; range: AnalyticsRange }>) {
  const slices: DonutSlice[] = records.requestsByType.map((entry) => ({
    key: entry.type,
    label: REQUEST_TYPE_LABELS[entry.type],
    count: entry.count,
    className: TYPE_COLOURS[entry.type].stroke,
    fillClassName: TYPE_COLOURS[entry.type].fill,
  }));
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  return (
    <CardShell
      title={`What ${name} asks for`}
      description="Requests by type in this period"
      zoom={{ metric: "requests_created", range, split: "request_type" }}
    >
      {total === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">No request in this period.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-5">
          <Donut
            slices={slices}
            centre={{ value: String(total), caption: total === 1 ? "request" : "requests" }}
          />
          <ul className="m-0 grid min-w-0 flex-1 list-none gap-1.5 p-0 text-sm">
            {slices.map((slice) => (
              <li key={slice.key} className="flex items-center gap-2">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  viewBox="0 0 10 10"
                  className="size-2.5 shrink-0"
                >
                  <rect width="10" height="10" rx="3" className={slice.fillClassName} />
                </svg>
                <span className="flex-1">{slice.label}</span>
                <span className="font-medium">{slice.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </CardShell>
  );
}

export function SpendCard({
  records,
  range,
}: Readonly<{ records: Records; range: AnalyticsRange }>) {
  const paid = records.spendByMonth.reduce((sum, month) => sum + month.amountMinor, 0);
  const payments = records.spendByMonth.reduce((sum, month) => sum + month.payments, 0);
  return (
    <CardShell
      title="Spend"
      description={`Paid and not refunded, by month, ${rangePhrase(range)}`}
      zoom={{ metric: "amount_paid", range }}
    >
      {records.spendByMonth.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">No payment {rangePhrase(range)}.</p>
      ) : (
        <>
          <p className="m-0 text-sm text-muted-foreground">
            <span className="font-serif text-[28px] leading-8 font-medium text-foreground">
              {moneyText(paid)}
            </span>{" "}
            paid · {payments} {payments === 1 ? "payment" : "payments"}
          </p>
          <SpendChart months={records.spendByMonth} />
        </>
      )}
    </CardShell>
  );
}

export function ServiceCard({
  records,
  range,
}: Readonly<{ records: Records; range: AnalyticsRange }>) {
  const { service } = records;
  return (
    <CardShell
      title="Assistant and concierge"
      description="Messages and how fast the service answered"
      zoom={{ metric: "customer_messages", range }}
    >
      <ul className="m-0 grid list-none gap-1 p-0 text-sm">
        {messageRows(records.messages).map((row) => (
          <li key={row.label} className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-medium">{countText(row.count)}</span>
          </li>
        ))}
      </ul>
      <dl className="m-0 grid grid-cols-2 gap-3 border-t border-hairline pt-3 text-sm">
        <div>
          <dt className="text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase">
            Assistant replies
          </dt>
          <dd className="m-0 font-serif text-lg font-medium">
            {countText(service.assistantReplies)}
          </dd>
          <dd className="m-0 text-xs text-muted-foreground">
            {service.assistantFirstReplyMedianSeconds === null
              ? "No reply yet"
              : `First reply ${durationText(service.assistantFirstReplyMedianSeconds)} (median)`}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase">
            Wait for a person
          </dt>
          <dd className="m-0 font-serif text-lg font-medium">
            {service.waitForPersonMedianSeconds === null
              ? "No wait yet"
              : durationText(service.waitForPersonMedianSeconds)}
          </dd>
          <dd className="m-0 text-xs text-muted-foreground">
            Median, from the request to a person taking it
          </dd>
        </div>
      </dl>
    </CardShell>
  );
}
